// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Piano Intervals
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';

// Interval name type options:
//    longNames: Perfect 4th
//    genericNames: 4th
//    halfWholeNames: Half Step, Whole Step, minor third, etc...
//    ascendingDescending: ascending P4
//      The default is: (intervalname) + above/below.
//    directionOnly: if you want to specify interval name & drill a specific interval only.

class PianoIntervalsID extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = PianoIntervalsID.registry.skills;
    this.settings = {
      instructions: true,
      piano: {
        listeners: false,
        ariaLive: 'alert',
      },
      letters: {
        numberButtons: true,
        intervalQualityButtons: true,
        checkButton: true,
      },
      checkMark: {
        rightAnswerLength: 0.6,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.answer = {
      intervalName: '',
      intervalQuality: '',
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    if (options.halfWholeOnly === true) {
      const letters = this.settings.letters;

      letters.numberButtons = false;
      letters.intervalQualityButtons = false;
      letters.checkButton = false;

      letters.customButtons = [
        { text: 'HS', value: 'm2', hotKey: ['h', 'H'] },
        { text: 'WS', value: 'M2', hotKey: ['w', 'W'] },
        { text: 'Other', value: 'Other', hotKey: ['o', 'O'] },
      ];

      options.skills = ['half_steps_piano', 'whole_steps_piano'];
    }

    // copy our settings onto the prototype settings:
    utils.merge(this.settings.piano, {
      lowestNote: options.lowestNote,
      highestNote: options.highestNote,
    });

    this.skillCalculator = new SkillCalculator();
    super.init(displayDiv, options);
  }

  generateQuestions() {
    const options = this.options;
    this.questions = this.getQuestions(options);
    this.maxQuestions = this.questions.length;
  }

  getQuestions(options) {
    const drill = options.drill !== undefined && options.drill === true;
    return drill === true
      ? this.generateDrillQuestion(options)
      : this.generateManualQuestions(options);
  }

  generateManualQuestions(options) {
    return options.questions.map((question, index) => {
      const questionParts = question.split('/');
      const note = new Notation.Note(questionParts[0]);
      const interval = new Notation.Interval(questionParts[1]);
      const note2 = note.getNoteAt(interval);
      const skills = this.calculateSkill(interval);

      return {
        question: {
          note,
          note2,
          interval,
        },
        answer: {
          intervalName: `${interval.genericInterval}`,
          intervalQuality: interval.quality,
        },
        text: index + 1,
        skills,
      };
    });
  }

  generateDrillQuestion(options) {
    const generator = new QuestionGenerator();
    let questions = generator.generate(options.skills, options.maxQuestions, options);

    if (options.halfWholeOnly === true && options.decoyInterval !== undefined) {
      const decoys = generator.interval_helper(
        Math.floor(options.maxQuestions / 3),
        options,
        options.decoyInterval
      );
      questions = generator.rejectDuplicates(
        questions.concat(decoys),
        options.maxQuestions,
        options
      );
    }

    return questions.map((question, index) => {
      const note = question.note;
      const interval = question.interval;
      const note2 = note.getNoteAt(interval);
      const skills = this.calculateSkill(interval);

      return {
        question: {
          note,
          note2,
          interval,
        },
        answer: {
          intervalName: `${interval.genericInterval}`,
          intervalQuality: interval.quality,
        },
        text: index + 1,
        skills,
      };
    });
  }

  calculateSkill(interval) {
    if (this.optionsTrue('halfWholeOnly')) {
      return this.skillCalculator.getPianoHalfWholeSkill(interval);
    }
    return this.skillCalculator.getPianoIntervalSkill(interval);
  }

  questionText(interval) {
    const intervalText = this.intervalText(interval);
    const directionText = this.directionText(interval);
    const ascDsc =
      this.options.ascendingDescending !== undefined && this.options.ascendingDescending === true;

    if (ascDsc) {
      return `${utils.aOrAn(directionText)} ${directionText} ${intervalText}`;
    }
    return `${utils.aOrAn(intervalText)} ${intervalText} <strong>${directionText}</strong>`;
  }

  intervalText(interval) {
    // longNames: Perfect 4th, minor 2nd, Augmented octave
    if (this.options.longNames !== undefined && this.options.longNames === true) {
      return interval.longName();
    }

    if (this.options.halfWholeNames !== undefined && this.options.halfWholeNames === true) {
      return interval.halfWholeName();
    }

    if (this.options.genericNames !== undefined && this.options.genericNames === true) {
      return interval.longGenericName();
    }
    return interval.quality + interval.genericInterval;
  }

  directionText(interval) {
    if (this.optionsTrue('ascendingDescending')) {
      return interval.direction > 0 ? 'ascending' : 'descending';
    }
    return interval.direction > 0 ? 'above' : 'below';
  }

  newQuestion() {
    this.progressTracker.start(this.targetSpeed);

    const question = this.questions[this.questionNumber].question;
    const note1 = question.note;
    const note2 = question.note2;

    this.questionPrompt = this.getQuestionPrompt(question);
    this.piano.clearColors();
    this.piano.colorNote(note1.getMidiNumber());
    this.piano.colorNote(note2.getMidiNumber());

    super.newQuestion();
  }

  getQuestionPrompt(question) {
    const interval = question.interval;
    const note1 = question.note;
    const note2 = question.note2;
    const directionText = this.directionText(interval);
    const note1Text = note1.letter + note1.accidental;
    const note2Text = note2.letter + note2.accidental;
    const questionPrompt = this.options.questionPrompt;
    const testMode = this.optionsTrue('testMode');
    const halfWholeOnly = this.options.halfWholeOnly;

    if (testMode === true && halfWholeOnly === true) {
      return 'Identify interval you see';
    }
    if (testMode === true && halfWholeOnly === false) {
      return `Identify ${directionText} interval between ${note1Text} and ${note2Text}`;
    }
    if (testMode === false && halfWholeOnly === true) {
      return questionPrompt || `Identify interval you see. #{question} of ${this.questions.length}`;
    }
    return `Identify ${directionText} interval between ${note1Text} and ${note2Text}. Question #{question} of ${this.questions.length}`;
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      const questionParts = questionString.split('/');
      const note = new Notation.Note(questionParts[0]);
      const interval = new Notation.Interval(questionParts[1]);
      const note2 = note.getNoteAt(interval);
      const skills = this.calculateSkill(interval);
      const questions = [];

      questions.push({
        question: {
          note,
          note2,
          interval,
        },
        answer: {
          intervalName: `${interval.genericInterval}`,
          intervalQuality: interval.quality,
        },
        skills,
      });

      this.questions = questions;

      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  createCustomDivs() {
    if (this.options.halfWholeOnly === false) {
      this.createAnswerDiv('div', 'answerDiv', 2);
    }
  }

  createAnswerDiv(element, elementId, index) {
    this[elementId] = document.createElement(element);

    const div = this[elementId];
    const styleOptions = {
      fontSize: '4vw',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '10px',
    };

    div.id = elementId;
    div.innerHTML = '?';

    utils.merge(div.style, styleOptions);

    this.displayDiv.insertBefore(div, this.displayDiv.children[index]);
  }

  addListeners() {
    if (this.options.halfWholeOnly === true) {
      this.letterPanel.addCustomButtonCallback(this.checkHalfWholeAnswer, this);
    } else {
      this.letterPanel.addNumberCallback(this.addIntervalNameToAnswer, this);
      this.letterPanel.addIntervalQualityCallback(this.addIntervalQualityToAnswer, this);
      this.letterPanel.addCheckCallback(this.checkAnswer, this);
    }
  }

  addIntervalNameToAnswer(semitones) {
    this.answer.intervalName = semitones;

    return this.displayAnswer();
  }

  addIntervalQualityToAnswer(quality) {
    this.answer.intervalQuality = quality;

    return this.displayAnswer();
  }

  displayAnswer() {
    const answer = this.answer.intervalQuality + this.answer.intervalName;

    if (answer === '') {
      this.answerDiv.innerHTML = '?';
    } else {
      this.answerDiv.innerHTML = answer;
    }
  }

  checkHalfWholeAnswer(answer) {
    if (this.rightAnswerDelay) return;
    this.answerAttempts++;

    const testMode = this.optionsTrue('testMode');
    const question = this.questions[this.questionNumber].answer;
    const interval = `${question.intervalQuality}${question.intervalName}`;
    const correct =
      answer === interval || (answer === 'Other' && interval !== 'm2' && interval !== 'M2');

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.rightAnswer(1000);
    } else {
      this.wrongAnswer();
    }
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    this.answerAttempts++;

    const testMode = this.optionsTrue('testMode');
    const questionAnswer = this.questions[this.questionNumber].answer;
    const userAnswer = this.answer;
    const intervalName = questionAnswer.intervalName === userAnswer.intervalName;
    const intervalQuality = questionAnswer.intervalQuality === userAnswer.intervalQuality;
    const correct = intervalName && intervalQuality;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      this.clearAnswer();
      return;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1000);
      window.setTimeout(this.clearAnswer.bind(this), 1000);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  getAnswerAndData(correct) {
    const correctAnswer = this.questions[this.questionNumber].answer;
    const answerAndData = {
      question: this.questions[this.questionNumber].question,
      questionType: 'PianoIntervalsID',
      questionText: this.questionPrompt,
      questionSVG: this.piano.exportSVG(),
      studentAnswerText: `${this.answer.intervalName}${this.answer.intervalQuality}`,
      correctAnswerText: `${correctAnswer.intervalQuality}${correctAnswer.intervalName}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber].skills,
    };

    return answerAndData;
  }

  clearAnswer() {
    Object.keys(this.answer).forEach((i) => {
      if (this.answer.hasOwnProperty(i)) {
        this.answer[i] = '';
      }
    });

    this.answerDiv.innerHTML = '?';
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    if (this.options.halfWholeOnly === false) {
      this.displayDiv.removeChild(this.answerDiv);
    }
    this.piano.removeListeners();
    super.gameOver();
  }

  playAnswer() {
    const note = this.questions[this.questionNumber].question.note;
    const note2 = this.questions[this.questionNumber].question.note2;

    this.player.play(note.getMidiNumber(), { startTime: 0, stopTime: 1.2 });
    this.player.play(note2.getMidiNumber(), { startTime: 0, stopTime: 1.2 });
  }
}

PianoIntervalsID.registry = {
  description: 'Identify Half & Whole Steps on the Piano',
  skills: ['half_steps_piano', 'whole_steps_piano'],
  options: {
    lowestNote: 'G3',
    highestNote: 'C5',
    questionPrompt: 'What is the name of the interval between the highlighted notes?',
    drill: true,
    halfWholeNames: true,
    maxQuestions: 7,
    decoyInterval: 'm3',
    halfWholeOnly: true,
  },
};

export default PianoIntervalsID;
